<template>
    <div class="space-y-48">
        <section>
            <application-title :title="$t('applications.education')">
                <application-button
                    :condition="canManage"
                    :title="$t('general.add_subject', {subject: $t('applications.education')})"
                    icon="add"
                    @click="createEducation"
                />
            </application-title>

            <loading v-if="fetchingEducations"></loading>
            <ul v-else-if="educations.length" class="rounded-6 overflow-hidden">
                <li
                    v-for="education in educations"
                    :key="'education-' + education.id"
                    class="odd:bg-primary-5 relative"
                >
                    <a class="flex items-center p-16 cursor-pointer group" @click="viewEducation(education)">
                        <div class="mr-auto">
                            <div class="font-semibold group-hover:text-primary trans-ease mb-4">
                                {{ education.title }}
                                <template v-if="education.title && education.institution">
                                    -
                                </template>
                                {{ education.institution }}
                            </div>
                            <div v-if="education.from_date || education.to_date">
                                <template v-if="education.from_date">
                                    <span class="italic text-primary-80">{{ $t('general.from') }}</span>
                                    {{ education.fromMoment.locale(locale).format(dateFormat) }}
                                </template>
                                <template v-if="education.to_date">
                                    <span class="italic text-primary-80">{{ $t('general.to') }}</span>
                                    {{ education.toMoment.locale(locale).format(dateFormat) }}
                                </template>
                            </div>
                        </div>
                        <div class="flex pl-16">
                            <div class="h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease group-hover:text-primary mr-64">
                                <svg-icon class="max-w-16 max-h-16" type="visibility"></svg-icon>
                            </div>
                        </div>
                    </a>

                    <div v-if="canManage" class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                        <button class="pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease hover:text-primary-80" @click="editEducation(education)">
                            <svg-icon class="max-w-16 max-h-16" type="edit"></svg-icon>
                        </button>
                        <button class="pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease hover:text-semantic-error" @click="confirmDelete(education)">
                            <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                        </button>
                    </div>
                </li>
            </ul>
        </section>

        <section>
            <application-title :title="$t('companypage.experience')">
                <application-button
                    :condition="canManage"
                    :title="$t('general.add_subject', {subject: $t('companypage.experience')})"
                    icon="add"
                    @click="createExperience"
                />
            </application-title>

            <loading v-if="fetchingExperiences"></loading>
            <ul v-else-if="experiences.length" class="rounded-6 overflow-hidden">
                <li
                    v-for="experience in experiences"
                    :key="'experience-' + experience.id"
                    class="odd:bg-primary-5 relative"
                >
                    <a class="flex items-center p-16 cursor-pointer group" @click="viewExperience(experience)">
                        <div class="mr-auto">
                            <div class="font-semibold group-hover:text-primary trans-ease mb-4">
                                {{ experience.job_title }}
                                <template v-if="experience.job_title && experience.company">
                                    -
                                </template>
                                {{ experience.company }}
                            </div>
                            <div v-if="experience.date_from || experience.date_to">
                                <template v-if="experience.date_from">
                                    <span class="italic text-primary-80">{{ $t('general.from') }}</span>
                                    {{ experience.fromMoment.locale(locale).format(dateFormat) }}
                                </template>
                                <template v-if="experience.date_to">
                                    <span class="italic text-primary-80">{{ $t('general.to') }}</span>
                                    {{ experience.toMoment.locale(locale).format(dateFormat) }}
                                </template>
                            </div>
                        </div>
                        <div class="flex pl-16">
                            <div class="h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease group-hover:text-primary mr-64">
                                <svg-icon class="max-w-16 max-h-16" type="visibility"></svg-icon>
                            </div>
                        </div>
                    </a>

                    <div v-if="canManage" class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                        <button class="pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease hover:text-primary-80" @click="editExperience(experience)">
                            <svg-icon class="max-w-16 max-h-16" type="edit"></svg-icon>
                        </button>
                        <button class="pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease hover:text-semantic-error" @click="confirmDelete(experience)">
                            <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                        </button>
                    </div>
                </li>
            </ul>

        </section>

        <experience-editor
            v-if="canManage && creatingExperience"
            :application="application"
            @close="stopCreatingExperience"
        ></experience-editor>

        <experience-editor
            v-if="canManage && editingExperience"
            :application="application"
            :experience="editingExperience"
            @close="stopEditingExperience"
        ></experience-editor>

        <education-editor
            v-if="canManage && creatingEducation"
            :application="application"
            @close="stopCreatingEducation"
        ></education-editor>

        <education-editor
            v-if="canManage && editingEducation"
            :application="application"
            :education="editingEducation"
            @close="stopEditingEducation"
        ></education-editor>

        <popup-modal-confirm
            v-if="canManage && deleting"
            :confirmText="$t('general.delete_subject', {subject: $t('applications.' + (isExperience(deleting) ? 'experience' : 'education'))})"
            :message="$t('general.confirm_delete_subject', {subject: $t('applications.' + (isExperience(deleting) ? 'this_experience' : 'this_education'))})"
            show-exclamation
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="deleteResource(deleting)"
        >
        </popup-modal-confirm>

        <popup-modal
            v-if="viewingEducation"
            :description="formatDateDescription(viewingEducation)"
            :title="viewingEducation.title + (viewingEducation.title && viewingEducation.institution ? ' - ' : '') + viewingEducation.institution"
            position="center"
            @close="closeEducation"
        >
            <div class="w-768 text-body px-32">
                <h2 class="font-semibold text-body mb-16">{{ $t('applications.short_education_description') }}</h2>
                <p class="text-body-sm" v-if="viewingEducation?.short_description">
                    {{ viewingEducation.short_description }}
                </p>
                <p v-else class="text-primary-80">...</p>
            </div>
        </popup-modal>

        <popup-modal
            v-if="viewingExperience"
            :description="formatDateDescription(viewingExperience)"
            :title="viewingExperience.job_title + (viewingExperience.job_title && viewingExperience.company ? ' - ' : '') + viewingExperience.company"
            position="center"
            @close="closeExperience"
        >
            <div class="w-768 text-body px-32">
                <div class="mb-32">
                    <p class="font-semibold text-body mb-12">{{ $t('applications.short_job_description') }}</p>
                    <p v-if="viewingExperience?.job_description">
                        {{ viewingExperience.job_description }}
                    </p>
                    <p v-else class="text-primary-80">...</p>
                </div>
                <div class="">
                    <p class="font-semibold text-body mb-12">{{ $t('applications.tasks') }}</p>
                    <p v-if="viewingExperience?.tasks">
                        {{ viewingExperience.tasks }}
                    </p>
                    <p v-else class="text-primary-80">...</p>
                </div>
            </div>

        </popup-modal>

    </div>
</template>

<script>
import * as educations from '../services/EducationService';
import * as experiences from '../services/ExperienceService';
import Application from '../models/Application';
import Education from '../models/Education';
import {normalizeJson} from '../../shared/helpers';
import Experience from '../models/Experience';
import Loading from './ui/Loading.vue';
import SimpleButton from './ui/SimpleButton.vue';
import moment from 'moment';
import ExperienceEditor from './ui/ExperienceEditor.vue';
import SvgIcon from './ui/SvgIcon.vue';
import EducationEditor from './ui/EducationEditor.vue';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import PopupModal from './ui/PopupModal.vue';
import ApplicationButton from "./ui/ApplicationButton.vue";
import ApplicationTitle from "./applications/ApplicationTitle.vue";
import {mapStores} from "pinia";
import {useRepo} from "pinia-orm";
import {useMainStore} from "../state/store";

export default {
    components: {
        ApplicationTitle,
        ApplicationButton,
        'loading': Loading,
        'simple-button': SimpleButton,
        'experience-editor': ExperienceEditor,
        'education-editor': EducationEditor,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'popup-modal': PopupModal,
    },
    props: {
        application: {
            type: Application,
            required: true,
        },
        canManage: Boolean,
    },
    data: function () {
        return {
            fetchingEducations: true,
            fetchingExperiences: true,
            creatingEducation: false,
            creatingExperience: false,
            editingEducation: null,
            editingExperience: null,
            deleting: null,
            performingDeleteRequest: false,
            viewingEducation: null,
            viewingExperience: null,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        locale() {
            return this.mainStore.locale;
        },
        dateFormat() {
            return this.mainStore.dateFormat;
        },
        educations() {
            return useRepo(Education)
                .where('application_id', this.application.id)
                .get()
                .sort((a, b) => moment(a.date_from).isAfter(b.date_from) ? 1 : -1);
        },
        experiences() {
            return useRepo(Experience)
                .where('application_id', this.application.id)
                .get()
                .sort((a, b) => moment(a.date_from).isBefore(b.date_from) ? 1 : -1);
        },
    },
    methods: {
        isExperience(resource) {
            return resource instanceof Experience;
        },
        fetchEducations() {
            this.fetchingEducations = true;

            educations.getApplicationEducations(this.application.id)
                .then(result => {
                    return useRepo(Education).save(normalizeJson(result.data));
                }).then(() => {
                    this.fetchingEducations = false;
                });
        },
        fetchExperiences() {
            this.fetchingExperiences = true;

            experiences.getApplicationExperiences(this.application.id)
                .then(result => {
                    return useRepo(Experience).save(normalizeJson(result.data));
                }).then(() => {
                    this.fetchingExperiences = false;
                });
        },
        createEducation() {
            this.creatingEducation = true;
        },
        createExperience() {
            this.creatingExperience = true;
        },
        editEducation(education) {
            this.editingEducation = education;
        },
        editExperience(experience) {
            this.editingExperience = experience;
        },
        stopCreatingEducation() {
            this.creatingEducation = false;
        },
        stopCreatingExperience() {
            this.creatingExperience = false;
        },
        stopEditingEducation() {
            this.editingEducation = null;
        },
        stopEditingExperience() {
            this.editingExperience = null;
        },
        confirmDelete(resource) {
            this.deleting = resource;
        },
        cancelDelete() {
            this.deleting = null;
        },
        deleteResource(resource) {
            this.performingDeleteRequest = true;

            let service = this.isExperience(resource) ? experiences : educations;
            let model = this.isExperience(resource) ? Experience : Education;

            service.destroy(resource.id).then(() => {
                useRepo(model).destroy(resource.id);
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            });
        },
        viewEducation(education) {
            this.viewingEducation = education;
        },
        viewExperience(experience) {
            this.viewingExperience = experience;
        },
        closeEducation() {
            this.viewingEducation = null;
        },
        closeExperience() {
            this.viewingExperience = null;
        },
        formatDateDescription(item) {
            let str = '';

            if (item.date_from) {
                str += this.$t('general.from') + ' <span class=&quot;text-primary text-primary-60 font-semibold&quot;>' + item.fromMoment.locale(this.locale).format(this.dateFormat) + '</span> ';
            }
            if (item.date_to) {
                str += this.$t('general.to') + ' <span class=&quot;text-primary text-primary-60 font-semibold&quot;>' + item.toMoment.locale(this.locale).format(this.dateFormat) + '</span>';
            }

            return str;
        },
    },
    mounted() {
        this.fetchEducations();
        this.fetchExperiences();
    },
};
</script>
